// Centralized API client for the server browser.
import axios from "axios";

// We ALWAYS call the backend same-origin via a relative `/api` base URL.
// This is the right default for every deployment shape this app ships in:
//
//   • Emergent preview env: Kubernetes ingress routes `/api/*` → FastAPI :8001
//     on the same preview host.
//   • Cloudflare unified-origin production (server-recon.com + www): the CF
//     worker proxies `/api/*` to the backend on the same host, so apex and
//     www each stay same-origin — no CORS ever involved.
//   • Local dev with CRA proxy (optional `proxy` in package.json): CRA
//     forwards `/api/*` to FastAPI on :8001.
//
// Hard-coding a cross-origin URL via REACT_APP_BACKEND_URL caused the
// production "flash then black" bug: the bundle was baked to the apex
// (`https://server-recon.com`), but when users hit `www.server-recon.com`
// every /api/* call became a credentialed cross-origin request that the
// browser rejected (backend was returning `Access-Control-Allow-Origin: *`
// combined with credentials, which is invalid). Keeping the base relative
// makes CORS physically impossible to break on this surface.
export const API = "/api";
export const BACKEND_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export const api = axios.create({
  baseURL: API,
  timeout: 20000,
  withCredentials: true,
});

export const fetchGames = async () => (await api.get("/games")).data;
export const fetchServers = async (params) => (await api.get("/servers", { params })).data;
export const fetchServer = async (id) => (await api.get(`/servers/${encodeURIComponent(id)}`)).data;
export const fetchServerHistory = async (id, bucket, window) =>
  (await api.get(`/servers/${encodeURIComponent(id)}/history`, {
    params: {
      ...(bucket ? { bucket } : {}),
      ...(window ? { window } : {}),
    },
  })).data;
export const fetchServerDetails = async (id) =>
  (await api.get(`/servers/${encodeURIComponent(id)}/details`)).data;
export const fetchServerDowntime = async (id, days = 30) =>
  (await api.get(`/servers/${encodeURIComponent(id)}/downtime`, { params: { days } })).data;
export const joinServer = async (id) => (await api.post(`/servers/${encodeURIComponent(id)}/join`)).data;
export const fetchStats = async (gameId) => (await api.get("/stats", { params: { game_id: gameId } })).data;
export const fetchTrends = async (gameId, days = 30) =>
  (await api.get(`/trends/${encodeURIComponent(gameId)}`, {
    params: { days },
    timeout: 60000,
  })).data;
export const fetchRankings = async (gameId, region = null, limit = 50) =>
  (await api.get(`/rankings/${encodeURIComponent(gameId)}`, {
    params: { ...(region && region !== "all" ? { region } : {}), limit },
    timeout: 60000,
  })).data;

// Favorites
export const fetchFavorites = async () => (await api.get("/favorites")).data;
export const addFavorite = async (server_id, game_id) =>
  (await api.post("/favorites", { server_id, game_id })).data;
export const removeFavorite = async (server_id) =>
  (await api.delete(`/favorites/${encodeURIComponent(server_id)}`)).data;

// Custom Minecraft servers
export const fetchCustomMc = async () => (await api.get("/custom-mc")).data;
export const addCustomMc = async (payload) => (await api.post("/custom-mc", payload)).data;
export const deleteCustomMc = async (id) => (await api.delete(`/custom-mc/${id}`)).data;
